/* eslint-disable max-lines */
/* eslint-disable complexity */
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { CartProductItem } from '@westwing/ui-kit/ContentFlyout/CartFlyout/types';
import useExpiryTime from '../hooks/useExpiryTime';
import useCartTimer from '../hooks/useCartTimer';
import { EventData, EventEntry, generateWWEvents } from '../WWEvents';
import RequestCartInfo from '../helpers/RequestCartInfo';
import { useClubContext } from '../hooks/ClubContext';
import { ClubLink } from '../types/clubLinksTypes';
import useSignupModal from '../hooks/useSignupModalTrigger';
import CartReminder from '../CartReminder';
import CartExpiryCounter from '../CartExpiryCounter';
import { useOneCartContext } from '../hooks/OneCartContext';
import { getCartProducts, getGrandTotal } from '../helpers/cartV2.helpers';
import { getOneCartLink, getOpenClubOneCartLink } from '../clubLinks';
import { noop } from '../../utils/noop';
import useIsCartExpired from '../hooks/useIsCartExpired';
import { debounce } from '../../helpers/debounce';
import CartFlyoutWrapper from './CartFlyoutWrapper';
import {
    Wrapper,
    BadgeWrapper,
    Timer,
    Badgebubble,
    StyledCart,
    StyledBasket,
    CartIconContainer,
    RotatingSpinner,
} from './cartIcon.styles';
import { transformOneCartResponseToMiniCartData, isClubHeaderInBella, calculateSecondsLeft } from './CartIcon.helper';
import HeaderIconLink from 'Components/IconLink';
import ErrorBoundary from 'Components/ErrorBoundary';
import { useHeaderContext } from 'Components/HeaderContext';
import { LoginState } from 'club/types/LoginState';
import { HeaderVariant } from 'club/HeaderStandalone';

export interface CartIconProps {
    formattedQuantity: string;
    secondsLeft: number;
    remindAt: number;
    showMinicartAt: number;
    products: CartProductItem[];
    totalPrice: string;
    isOneCart: boolean;
    loginState?: LoginState;
    clubStaticHost: string;
    headerIcon: string;
    variant: HeaderVariant;
}

export const parseSeconds = (seconds: number): string => {
    const date = new Date();
    date.setHours(1, 0, seconds);
    return date.toISOString().substr(14, 5);
};

const CartIcon = ({
    formattedQuantity,
    secondsLeft,
    remindAt,
    showMinicartAt,
    products = [],
    totalPrice,
    isOneCart,
    loginState,
    clubStaticHost,
    headerIcon = 'cart',
    variant,
}: CartIconProps): JSX.Element => {
    const [serverRemainingSeconds, setServerRemainingSeconds] = React.useState(secondsLeft);
    const [timer, setTimer] = React.useState('');
    const { startTimer, remainingTime, resetTimer } = useCartTimer(remindAt, showMinicartAt);
    const [quantity, setQuantity] = React.useState(formattedQuantity);
    const { links, isOpenClubEnabled, serverSide } = useClubContext();
    const cartLink = React.useMemo(() => {
        if (!serverSide && isOneCart) {
            return isOpenClubEnabled ? getOpenClubOneCartLink() : getOneCartLink();
        }
        return links[ClubLink.CART].path;
    }, [isOneCart, isOpenClubEnabled, links, serverSide]);
    const { t } = useHeaderContext();
    const [isCartFlyoutVisible, setIsCartFlyoutVisible] = React.useState(false);
    const {
        isOneCartEnabled,
        metadata,
        fetchCartMetaData,
        clearOneCartContext,
        data: oneCartData,
        fetchCartData: fetchOneCartData,
        isFetching: isOneCartDataFetching,
    } = useOneCartContext();
    const { country } = useOneCartContext();
    const [isFlyoutCartIntersecting, setFlyoutIntersecting] = React.useState(false);

    const [miniCartFlyoutProducts, setMiniCartFlyoutProducts] = React.useState(
        isOneCartEnabled && oneCartData?.cart?.itemList?.length ? getCartProducts(oneCartData, country) : products
    );
    const [miniCartSubTotal, setMiniCartSubTotal] = React.useState(
        isOneCartEnabled && oneCartData?.cart?.summary ? getGrandTotal(oneCartData.cart?.summary, country) : totalPrice
    );
    const [isDeliveryUpdateVisible, setIsDeliveryUpdateVisible] = React.useState(false);
    let signupModalTrigger = useSignupModal();
    if (isOneCartEnabled) {
        signupModalTrigger = {};
    }
    const {
        trackingHelpers: {
            ga: { trackNonInteractiveGenericEvent },
        },
    } = useHeaderContext();
    const cartV2ExpiryTime = oneCartData?.cart?.expiry;
    const { timeLeft: timeLeftCartV2 } = useExpiryTime(cartV2ExpiryTime);
    const isCartV2Expired = useIsCartExpired();
    const isClubHeaderInBellaCart = isClubHeaderInBella(window?.location);

    const updateQuantity = React.useCallback(
        (itemCount?: number) => {
            if (!itemCount) {
                setQuantity('');
                return;
            }
            setQuantity(itemCount.toString());
        },
        [setQuantity]
    );

    const updateTimer = React.useCallback(
        (seconds: number) => {
            const time = seconds === 0 ? '' : parseSeconds(seconds);
            setTimer(time);
        },
        [setTimer]
    );

    const showMiniCart = useCallback(
        (response: EventData['ADDED_TO_CART']): void => {
            if (response.delivery_times_changed) {
                setIsDeliveryUpdateVisible(true);
            }

            updateQuantity(response.total);
            updateTimer(response.seconds_left);
            setServerRemainingSeconds(response.seconds_left);
            setIsCartFlyoutVisible(true);
            setMiniCartFlyoutProducts(response.products);
            setMiniCartSubTotal(response.totalPrice);
        },
        [
            setIsDeliveryUpdateVisible,
            updateQuantity,
            updateTimer,
            setServerRemainingSeconds,
            setIsCartFlyoutVisible,
            setMiniCartFlyoutProducts,
            setMiniCartSubTotal,
        ]
    );

    // Hide miniCart after 7 seconds
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (isCartFlyoutVisible) {
            timeoutId = setTimeout(() => {
                setIsCartFlyoutVisible(false);
                setIsDeliveryUpdateVisible(false);
            }, 7000);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [isCartFlyoutVisible, setIsCartFlyoutVisible, setIsDeliveryUpdateVisible]);

    useEffect(() => {
        setMiniCartSubTotal(totalPrice);
    }, [totalPrice]);

    useEffect(() => {
        if (isOneCartEnabled && oneCartData?.cart?.itemList.length) {
            const oneCartProducts = getCartProducts(oneCartData, country);
            setMiniCartFlyoutProducts(oneCartProducts);
        }

        if (isOneCartEnabled && oneCartData?.cart?.summary) {
            setMiniCartSubTotal(getGrandTotal(oneCartData?.cart?.summary, country));
        }
    }, [country, isOneCartEnabled, oneCartData]);

    useEffect(() => {
        const WWEvents = generateWWEvents();
        const events: EventEntry[] = [
            {
                event: 'CART_EXPIRED',
                listener: () => {
                    updateQuantity();
                    setMiniCartSubTotal('');
                },
            },
            {
                event: 'ADDED_TO_CART',
                listener: async response => {
                    if (!isOneCart) {
                        showMiniCart(response);
                        return;
                    }
                    clearOneCartContext();
                    await fetchOneCartData();
                    if (oneCartData) {
                        showMiniCart(transformOneCartResponseToMiniCartData(oneCartData, country));
                    }
                },
            },
        ];

        events.forEach(event => WWEvents.push(event));
        return () => events.forEach(({ event, listener }) => WWEvents.remove(event, listener));
    }, [clearOneCartContext, country, fetchOneCartData, isOneCart, oneCartData, showMiniCart, updateQuantity]);

    useEffect(() => {
        const refreshOneCartEvent = {
            event: 'REFRESH_ONE_CART',
            listener: async (e: CustomEvent<{ shouldShowCartFlyout?: boolean }>) => {
                if (e?.detail?.shouldShowCartFlyout) {
                    clearOneCartContext();
                    await fetchOneCartData();
                    return;
                }

                fetchCartMetaData();
            },
        };

        document.addEventListener(refreshOneCartEvent.event, refreshOneCartEvent.listener, false);
        return () => document.removeEventListener(refreshOneCartEvent.event, refreshOneCartEvent.listener, false);
    }, [clearOneCartContext, fetchCartMetaData, fetchOneCartData]);

    useEffect(() => {
        if (serverSide) {
            return noop;
        }

        const getCartDetails = async () => {
            if (isOneCartEnabled) {
                await fetchOneCartData();
                if (oneCartData) {
                    setMiniCartFlyoutProducts(getCartProducts(oneCartData, country));
                    updateQuantity(oneCartData.cart.summary.quantity);
                }
                return;
            }

            const response = await RequestCartInfo(links[ClubLink.CART_REQUEST].path);
            if (response.success === true) {
                // eslint-disable-next-line @typescript-eslint/no-shadow,camelcase
                const { products = [], totalPrice = '', seconds_left = 0, total = 0 } = response.code;
                setServerRemainingSeconds(seconds_left);
                updateQuantity(total);

                setMiniCartSubTotal(totalPrice);
                setMiniCartFlyoutProducts(products);
            }
        };

        const { run, stop } = debounce(getCartDetails, 5000);

        window.addEventListener('focus', run);
        return () => {
            stop();
            window.removeEventListener('focus', run);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        updateTimer,
        updateQuantity,
        setMiniCartFlyoutProducts,
        setMiniCartSubTotal,
        links,
        isOneCartEnabled,
        serverSide,
    ]);

    useEffect(() => {
        if (serverRemainingSeconds > 0) {
            startTimer(serverRemainingSeconds);
        } else {
            resetTimer();
        }
    }, [startTimer, resetTimer, serverRemainingSeconds]);

    useEffect(() => {
        updateTimer(remainingTime);
    }, [remainingTime, updateTimer]);

    useEffect(() => {
        updateQuantity(parseInt(formattedQuantity, 10));
    }, [formattedQuantity, updateQuantity]);

    useEffect(() => {
        if (isOneCartEnabled && oneCartData) {
            updateQuantity(oneCartData?.cart.summary.quantity);
        }
    }, [isOneCartEnabled, oneCartData, updateQuantity]);

    useEffect(() => {
        setServerRemainingSeconds(secondsLeft);
    }, [secondsLeft]);

    useEffect(() => {
        setMiniCartFlyoutProducts(products);
    }, [products]);

    useEffect(() => {
        if (metadata && metadata?.count) {
            const secondsLeftFromMetaData = calculateSecondsLeft(metadata.expiry || '');
            setServerRemainingSeconds(secondsLeftFromMetaData);
            updateTimer(secondsLeftFromMetaData);
            updateQuantity(metadata.count);
        }
    }, [metadata, updateQuantity, updateTimer]);

    const onMouseEnter = React.useCallback(() => {
        setIsCartFlyoutVisible(true);

        if (miniCartFlyoutProducts && miniCartFlyoutProducts.length > 0) {
            trackNonInteractiveGenericEvent('Header', { action: 'Hover Shopping Cart' });
        }
    }, [miniCartFlyoutProducts, trackNonInteractiveGenericEvent]);

    const onCartRestore = React.useCallback(
        timeLeft => {
            updateTimer(timeLeft);
            startTimer(timeLeft);
        },
        [startTimer, updateTimer]
    );

    const renderQuantity = () => {
        if (isOneCartEnabled && ((cartV2ExpiryTime && !timeLeftCartV2) || isOneCartDataFetching)) {
            return <RotatingSpinner />;
        }
        if (isOneCartEnabled && cartV2ExpiryTime && isCartV2Expired) {
            return '?';
        }
        return quantity;
    };

    return (
        <>
            <Wrapper>
                <HeaderIconLink
                    href={cartLink}
                    data-testid="oww-header-cart-button"
                    aria-label={t('OwwHeader_To Cart')}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={() => {
                        setIsCartFlyoutVisible(false);
                        setIsDeliveryUpdateVisible(false);
                    }}
                    onClick={() => trackNonInteractiveGenericEvent('Header', { action: 'Click Shopping Cart' })}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...signupModalTrigger}
                >
                    <BadgeWrapper>
                        <Badgebubble data-testid="oww-header-cart-icon-bubble">{renderQuantity()}</Badgebubble>
                        <CartIconContainer active={headerIcon}>
                            <StyledCart data-type="cart" />
                            <StyledBasket data-type="basket" />
                        </CartIconContainer>
                        {!isOneCartEnabled && <Timer data-testid="oww-header-cart-icon-timer">{timer}</Timer>}
                    </BadgeWrapper>
                </HeaderIconLink>
                {isOneCartEnabled && cartV2ExpiryTime ? (
                    <CartExpiryCounter
                        loginState={loginState}
                        isCartOverlayActive={isFlyoutCartIntersecting}
                        isCartExpired={isCartV2Expired}
                        timeLeft={timeLeftCartV2}
                        isOneCartDataFetching={isOneCartDataFetching}
                        isClubHeaderInBellaCart={isClubHeaderInBellaCart}
                    />
                ) : null}
                {miniCartFlyoutProducts && miniCartFlyoutProducts.length > 0 && variant !== 'cart' && (
                    <ErrorBoundary fallbackComponent={null}>
                        <CartFlyoutWrapper
                            isVisible={isCartFlyoutVisible}
                            amountOfItems={quantity ? parseInt(quantity, 10) : 0}
                            remainingTime={remainingTime}
                            remindAt={remindAt}
                            products={miniCartFlyoutProducts}
                            totalPrice={miniCartSubTotal}
                            isDeliveryUpdateVisible={isDeliveryUpdateVisible}
                            isCartExpired={isCartV2Expired}
                            clubStaticHost={clubStaticHost}
                            setFlyoutIntersecting={setFlyoutIntersecting}
                        />
                    </ErrorBoundary>
                )}
            </Wrapper>
            <CartReminder onCartRestore={onCartRestore} remainingTime={remainingTime} remindAt={remindAt} />
        </>
    );
};

export default CartIcon;
